import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useInvoiceDetail } from '../api/useInvoiceDetail';
import {
  getInvoiceQualitySummary,
  getResolvedIssueIds,
  saveResolvedIssueIds,
} from '../utils/dataQuality';
import { extractString } from '../utils/sanitizeData';

export default function InvoiceIssuesPage() {
  const { id: publicToken } = useParams<{ id?: string }>();
  const { data: invoice, isLoading } = useInvoiceDetail(publicToken);

  const [resolvedIds, setResolvedIds] = useState<Set<string>>(() =>
    publicToken ? getResolvedIssueIds(publicToken) : new Set()
  );
  const [showResolved, setShowResolved] = useState(false);

  const qualitySummary = useMemo(() => {
    if (!invoice) {
      return {
        totalIssues: 0,
        criticalCount: 0,
        warningCount: 0,
        issues: [],
        badgeType: 'clean' as const,
        badgeLabel: '',
      };
    }
    return getInvoiceQualitySummary(invoice);
  }, [invoice]);

  const additionalFields = (invoice?.fields?.additional_fields ?? {}) as Record<string, unknown>;
  const invoiceId = extractString(
    invoice?.fields?.invoice_meta?.invoice_id ||
      invoice?.fields?.invoice_id ||
      additionalFields['InvoiceId'] ||
      additionalFields['invoice_id'],
    '—'
  );

  const vendorName = extractString(
    invoice?.vendor_name || invoice?.fields?.vendor?.name,
    'Unknown Vendor'
  );

  const invoiceDate = extractString(
    invoice?.fields?.invoice_meta?.invoice_date ||
      (invoice?.fields ? '' : invoice?.invoice_date),
    '—'
  );

  const totalAmount = extractString(
    invoice?.invoice_total?.amount || invoice?.fields?.totals?.invoice_total,
    '0.00'
  );
  const currency = extractString(
    invoice?.invoice_total?.currency || invoice?.fields?.totals?.currency,
    'EUR'
  );

  const handleToggleResolve = (issueId: string) => {
    if (!publicToken) return;
    setResolvedIds((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) {
        next.delete(issueId);
      } else {
        next.add(issueId);
      }
      saveResolvedIssueIds(publicToken, next);
      return next;
    });
  };

  const handleResolveAll = () => {
    if (!publicToken || qualitySummary.issues.length === 0) return;
    const allIds = new Set(qualitySummary.issues.map((i) => i.id));
    setResolvedIds(allIds);
    saveResolvedIssueIds(publicToken, allIds);
  };

  const activeIssues = qualitySummary.issues.filter((iss) => !resolvedIds.has(iss.id));
  const displayedIssues = showResolved
    ? qualitySummary.issues
    : activeIssues;

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      {/* Top Breadcrumb & Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to={`/invoices/${publicToken}`}
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-brand-vib hover:text-brand-vib-hover dark:text-brand-light transition-colors"
        >
          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          Back to Invoice
        </Link>

        <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
          / DATA QUALITY
        </div>
      </div>

      {/* Main Title & Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white tracking-tight">
            Data Quality Issues
          </h1>
          <p className="text-sm font-medium text-brand-soft dark:text-bodydark2 mt-0.5">
            {vendorName}
          </p>
        </div>

        {activeIssues.length > 0 && (
          <button
            type="button"
            onClick={handleResolveAll}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover shadow-sm transition-colors cursor-pointer"
          >
            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Resolve All
          </button>
        )}
      </div>

      {/* Invoice Overview Card */}
      <div className="rounded-2.5xl border border-brand-line bg-white shadow-card dark:border-strokedark dark:bg-boxdark p-5 sm:p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-brand-line dark:divide-strokedark">
          <div className="pt-2 sm:pt-0 sm:px-3">
            <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand-soft dark:text-bodydark2 block mb-1">
              Vendor
            </span>
            <div className="text-sm font-bold text-brand-navy dark:text-white truncate">
              {vendorName}
            </div>
          </div>

          <div className="pt-2 sm:pt-0 sm:px-3">
            <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand-soft dark:text-bodydark2 block mb-1">
              Invoice Date
            </span>
            <div className="text-sm font-mono text-brand-navy dark:text-white">
              {invoiceDate}
            </div>
          </div>

          <div className="pt-2 sm:pt-0 sm:px-3">
            <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand-soft dark:text-bodydark2 block mb-1">
              Invoice Total
            </span>
            <div className="text-sm font-mono font-bold text-brand-navy dark:text-white">
              {totalAmount} {currency}
            </div>
          </div>

          <div className="pt-2 sm:pt-0 sm:px-3">
            <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand-soft dark:text-bodydark2 block mb-1">
              Invoice #
            </span>
            <div className="text-sm font-mono text-brand-navy dark:text-white truncate">
              {invoiceId}
            </div>
          </div>
        </div>
      </div>

      {/* Issues Section Container */}
      <div className="rounded-2.5xl border border-brand-line bg-white shadow-card dark:border-strokedark dark:bg-boxdark overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-brand-line dark:border-strokedark flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white flex items-center gap-2">
              <span>🛡️</span>
              <span>ISSUES</span>
            </h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-mono font-semibold bg-brand-blue-tint text-brand-vib dark:bg-brand-900/40 dark:text-brand-300 border border-brand-vib/20">
              {activeIssues.length} {activeIssues.length === 1 ? 'Issue' : 'Issues'}
            </span>
          </div>

          <label className="inline-flex items-center gap-2 text-xs font-medium text-brand-soft dark:text-bodydark2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="rounded border-gray-300 text-brand-vib focus:ring-brand-vib"
            />
            <span>Show resolved</span>
          </label>
        </div>

        {/* List of Issue Items */}
        <div className="divide-y divide-brand-line dark:divide-strokedark">
          {isLoading ? (
            <div className="p-8 space-y-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="animate-pulse space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-strokedark rounded w-48" />
                  <div className="h-3 bg-gray-200 dark:bg-strokedark rounded w-full" />
                </div>
              ))}
            </div>
          ) : displayedIssues.length === 0 ? (
            <div className="py-12 px-5 text-center">
              <div className="flex flex-col items-center justify-center space-y-2">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-lg">
                  ✓
                </div>
                <p className="text-sm font-semibold text-brand-navy dark:text-white">
                  No Data Quality Issues
                </p>
                <p className="text-xs text-brand-soft max-w-sm">
                  {resolvedIds.size > 0
                    ? 'All detected data quality issues for this invoice have been resolved and verified.'
                    : 'All automated OCR and data integrity audits passed with 100% confidence.'}
                </p>
              </div>
            </div>
          ) : (
            displayedIssues.map((issue) => {
              const isResolved = resolvedIds.has(issue.id);
              const isCritical = issue.severity === 'critical';

              return (
                <div
                  key={issue.id}
                  className={`p-4 sm:p-5 transition-colors ${
                    isResolved ? 'opacity-50 bg-gray-50/50 dark:bg-boxdark-2/30' : 'hover:bg-brand-bg/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm">{isCritical ? '🔴' : '⚠️'}</span>
                        <span className="font-mono font-bold text-sm text-brand-navy dark:text-white">
                          {issue.field}
                        </span>
                        <span
                          className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border uppercase ${
                            isCritical
                              ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border-rose-200 dark:border-rose-800'
                              : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                          }`}
                        >
                          {issue.severity}
                        </span>
                      </div>

                      <p className="text-xs sm:text-sm text-[#3b4569] dark:text-bodydark leading-relaxed">
                        {issue.message}
                      </p>

                      <div className="flex items-center gap-1 text-[11px] font-mono text-brand-soft dark:text-bodydark2 pt-1">
                        <span>🤖</span>
                        <span>{issue.detectedAt}</span>
                      </div>
                    </div>

                    <div>
                      <button
                        type="button"
                        onClick={() => handleToggleResolve(issue.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer border ${
                          isResolved
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                            : 'bg-white text-brand-navy dark:bg-boxdark dark:text-white border-brand-line dark:border-strokedark hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 shadow-xs'
                        }`}
                      >
                        ✓ {isResolved ? 'Resolved' : 'Resolve'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Info Callout */}
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-brand-blue-tint dark:bg-brand-900/20 border border-brand-vib/15 dark:border-brand-800 text-xs text-[#3b4569] dark:text-bodydark leading-relaxed">
        <span className="text-base leading-none">ℹ️</span>
        <div>
          <span>
            <strong>Resolving an issue marks it as reviewed</strong> — it does not change the extracted data. To correct values, use{' '}
          </span>
          <Link
            to={`/invoices/${publicToken}/edit`}
            className="font-semibold text-brand-vib underline hover:text-brand-vib-hover dark:text-brand-light"
          >
            Edit Data
          </Link>
          <span> on the invoice.</span>
        </div>
      </div>
    </div>
  );
}
